var _react_refresh_temp_1, _react_refresh_temp_2;
var _react_refresh_temp_3;
_react_refresh_temp_3 = $RefreshSig$();
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect, useCallback, useMemo, useRef, } from 'react';
import { useTranslation } from 'react-i18next';
import { createSelector } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import ErrorPage from '../extra-pages/Error';
import Loading from '../extra-pages/Loading';
import Footer from '../footer';
import Header from '../header';
import MainArea from '../main-area';
import sendAPIRequest from '../../helpers/api/plugNmeetAPI';
import { store, useAppDispatch, useAppSelector } from '../../store';
import { addServerVersion, addToken } from '../../store/slices/sessionSlice';
import StartupJoinModal from './joinModal';
import useLivekitConnect from '../../helpers/livekit/hooks/useLivekitConnect';
import AudioNotification from './audioNotification';
import useKeyboardShortcuts from '../../helpers/hooks/useKeyboardShortcuts';
import useDesignCustomization from '../../helpers/hooks/useDesignCustomization';
import useWatchWindowSize from '../../helpers/hooks/useWatchWindowSize';
import useWatchVisibilityChange from '../../helpers/hooks/useWatchVisibilityChange';
import WaitingRoomPage from '../waiting-room/room-page';
import { updateIsActiveChatPanel } from '../../store/slices/bottomIconsActivitySlice';
import useThemeSettings from '../../helpers/hooks/useThemeSettings';
import { VerifyTokenReq, VerifyTokenRes, } from '../../helpers/proto/plugnmeet_common_api_pb';
import { getAccessToken } from '../../helpers/utils';
const waitingForApprovalSelector = createSelector(_react_refresh_temp_1 = (state) => { var _a; return (_a = state.session.currentUser) === null || _a === void 0 ? void 0 : _a.metadata; }, (metadata) => metadata === null || metadata === void 0 ? void 0 : metadata.wait_for_approval);
$RefreshReg$(_react_refresh_temp_1, "waitingForApprovalSelector$createSelector");
const App = () => {
    _react_refresh_temp_3();
    const dispatch = useAppDispatch();
    const { t, i18n } = useTranslation();
    // make sure we're using correct body dir
    document.dir = i18n.dir();
    const toastId = useRef(null);
    const [loading, setLoading] = useState(true);
    // it could be recorder or RTMP bot
    const [isRecorder, setIsRecorder] = useState(false);
    const [userTypeClass, setUserTypeClass] = useState('participant');
    const [livekitInfo, setLivekitInfo] = useState();
    const [currentConnection, setCurrentConnection] = useState();
    const waitForApproval = useAppSelector(waitingForApprovalSelector);
    // some custom hooks
    const { error, setError, roomConnectionStatus, setRoomConnectionStatus, startLivekitConnection, } = useLivekitConnect();
    useKeyboardShortcuts(currentConnection === null || currentConnection === void 0 ? void 0 : currentConnection.room);
    useDesignCustomization();
    useWatchVisibilityChange();
    const { deviceClass, orientationClass, screenHeight } = useWatchWindowSize(currentConnection === null || currentConnection === void 0 ? void 0 : currentConnection.room);
    useThemeSettings();
    useEffect(() => {
        const accessToken = getAccessToken();
        let timeout;
        if (!accessToken) {
            setLoading(false);
            setError({
                title: t('app.token-missing-title'),
                text: t('app.token-missing-des'),
            });
        }
        // else if (
        //   window.location.protocol === 'http:' &&
        //   window.location.hostname !== 'localhost'
        // ) {
        //   setLoading(false);
        //   setError({
        //     title: t('app.require-ssl-title'),
        //     text: t('app.require-ssl-des'),
        //   });
        // }
        else {
            const verifyToken = async () => {
                var _a;
                let res;
                try {
                    const r = await sendAPIRequest('verifyToken', new VerifyTokenReq({
                        isProduction: IS_PRODUCTION,
                    }).toBinary(), false, 'application/protobuf', 'arraybuffer');
                    res = VerifyTokenRes.fromBinary(new Uint8Array(r));
                }
                catch (error) {
                    console.error(error);
                    setRoomConnectionStatus('ready');
                    setLoading(false);
                    setError({
                        title: t('app.verification-failed-title'),
                        text: t('app.token-not-valid'),
                    });
                    return;
                }
                setRoomConnectionStatus('ready');
                setLoading(false);
                if (res.status && res.livekitHost && res.token) {
                    // we'll store token that we received from URL
                    dispatch(addToken(accessToken));
                    dispatch(addServerVersion((_a = res.serverVersion) !== null && _a !== void 0 ? _a : ''));
                    // for livekit need to use generated token & host
                    setLivekitInfo({
                        livekit_host: res.livekitHost,
                        token: res.token,
                        enabledE2EE: res.enabledE2ee,
                    });
                }
                else {
                    setError({
                        title: t('app.verification-failed-title'),
                        text: t(res.msg),
                    });
                }
            };
            if (!currentConnection) {
                setRoomConnectionStatus('checking');
                timeout = setTimeout(() => {
                    verifyToken();
                }, 300);
            }
        }
        return () => {
            if (timeout) {
                clearTimeout(timeout);
            }
        };
    }, [t, dispatch, currentConnection, setError, setRoomConnectionStatus]);
    useEffect(() => {
        if (roomConnectionStatus === 'connecting' ||
            roomConnectionStatus === 'checking') {
            setLoading(true);
        }
        else if (roomConnectionStatus === 're-connecting') {
            //eslint-disable-next-line
            // @ts-ignore
            toastId.current = toast.loading(t('notifications.room-disconnected-reconnecting'), {
                type: 'warning',
                closeButton: false,
                autoClose: false,
            });
        }
        else {
            setLoading(false);
            if (toastId.current) {
                toast.dismiss(toastId.current);
            }
        }
    }, [roomConnectionStatus]);
    useEffect(() => {
        var _a, _b;
        if (roomConnectionStatus === 'connected') {
            if ((currentConnection === null || currentConnection === void 0 ? void 0 : currentConnection.room.localParticipant.identity) === 'RECORDER_BOT' ||
                (currentConnection === null || currentConnection === void 0 ? void 0 : currentConnection.room.localParticipant.identity) === 'RTMP_BOT') {
                setIsRecorder(true);
                dispatch(updateIsActiveChatPanel(false));
            }
            if ((_b = (_a = store.getState().session.currentUser) === null || _a === void 0 ? void 0 : _a.metadata) === null || _b === void 0 ? void 0 : _b.is_admin) {
                setUserTypeClass('admin');
            }
        }
    }, [roomConnectionStatus]);
    const renderMainApp = useCallback(() => {
        if (currentConnection) {
            return (_jsxs("div", { className: "plugNmeet-app overflow-hidden h-screen", children: [!isRecorder ? _jsx(Header, { currentRoom: currentConnection.room }) : null, _jsx(MainArea, { isRecorder: isRecorder, currentConnection: currentConnection }), _jsx(Footer, { currentRoom: currentConnection.room, isRecorder: isRecorder }), _jsx(AudioNotification, {})] }));
        }
        return null;
    }, [isRecorder, currentConnection]);
    const onCloseStartupModal = async () => {
        if (livekitInfo) {
            const currentConnection = startLivekitConnection(livekitInfo);
            setCurrentConnection(currentConnection);
        }
    };
    const renderElms = useMemo(() => {
        if (loading) {
            return _jsx(Loading, { text: t(('app.' + roomConnectionStatus)) });
        }
        else if (error && !loading) {
            return _jsx(ErrorPage, { title: error.title, text: error.text });
        }
        else if (roomConnectionStatus === 'connected' ||
            roomConnectionStatus === 're-connecting') {
            if (waitForApproval) {
                return _jsx(WaitingRoomPage, {});
            }
            return renderMainApp();
        }
        else if (roomConnectionStatus === 'ready') {
            return _jsx(StartupJoinModal, { onCloseModal: onCloseStartupModal });
        }
        else {
            return null;
        }
    }, [loading, error, roomConnectionStatus, waitForApproval, renderMainApp]);
    return (_jsx("div", { className: `${orientationClass} ${deviceClass} ${userTypeClass} dark:bg-darkPrimary/70`, style: { height: screenHeight }, children: renderElms }));
};
_react_refresh_temp_2 = App;
$RefreshReg$(_react_refresh_temp_2, "App");
_react_refresh_temp_3(App, "VPH/6yCrm01nc95w7I2+L2Bjnug=", false, () => [useAppDispatch, useTranslation, useAppSelector, useLivekitConnect, useKeyboardShortcuts, useDesignCustomization, useWatchVisibilityChange, useWatchWindowSize, useThemeSettings]);
export default App;
